import { useId, useRef, useState } from 'react'
import { useAction, useMutation, useQuery } from 'convex/react'
import { ConvexError } from 'convex/values'
import type { FunctionReturnType } from 'convex/server'
import { Link } from 'react-router-dom'
import { api } from '../../../convex/_generated/api'
import type { Id } from '../../../convex/_generated/dataModel'
import { parseRomeAppointment, romeAppointmentFields } from '../../../shared/deliveryAppointment'
import { getPrimoUpAttachmentValidationError, PRIMOUP_ATTACHMENT_ACCEPT } from '../../../shared/primoUpIntegration'
import { Button } from '../ui/button'
import { Input } from '../ui/input'

type ActionData = NonNullable<FunctionReturnType<typeof api.isoReports.getPrescriptionAction>>
type ActionValues = {
  report_ids: Id<'iso_reports'>[]
  expected_row_id: Id<'prescription_rows'>
  expected_row_updated_at: number
  prova_id: number
  delivery_date: string
  delivery_time: string
  scansione_intraorale: boolean
  notes: string
}

export function IsoActionPanel({ prescriptionId, autoOpen = false, fromChat = false, onOpenPrescription }: {
  prescriptionId: Id<'prescriptions'>; autoOpen?: boolean; fromChat?: boolean; onOpenPrescription?: () => void
}) {
  const data = useQuery(api.isoReports.getPrescriptionAction, { prescription_id: prescriptionId })
  const confirm = useMutation(api.isoReports.confirmPrescriptionAction)
  const uploadUrl = useMutation(api.files.generateUploadUrl)
  const saveFile = useMutation(api.files.saveFileMetadata)
  const syncFile = useAction(api.primoup.syncPrescriptionAttachmentToPrimoUp)
  const uploaded = useRef(new Map<File, Id<'prescription_attachments'>>())
  const [expanded, setExpanded] = useState(autoOpen)
  const [notice, setNotice] = useState('')
  if (data === undefined) return <p role="status" className="p-4 text-sm text-gray-600">Caricamento richiesta ISO…</p>
  if (!data) return notice ? <p role="status" className="p-4 text-sm text-green-800">Risposta ISO sincronizzata con PrimoUp.</p> : null
  const required = data.reports.filter(report => report.action_status === 'required')
  const red = data.reports.some(report => report.action_kind === 'replan')
  const actionable = required.length > 0 && !data.blockedReason && data.canRespond && data.row
  return (
    <section id={fromChat ? undefined : 'iso-action'} aria-label="Intervento sulla prescrizione per segnalazione ISO"
      className={`min-w-0 rounded-lg border p-4 text-sm ${red ? 'border-red-200 bg-red-50 text-red-950' : 'border-amber-200 bg-amber-50 text-amber-950'}`}>
      <h2 className="font-semibold">{red ? 'Segnalazione ISO · lavorazione bloccata' : 'Segnalazione ISO · conferma richiesta'}</h2>
      <div className="mt-3 space-y-3">
        {data.reports.map(report => (
          <div key={report._id}>
            <p className="font-medium">{report.title}</p>
            <p className="mt-1 whitespace-pre-wrap break-words">{report.description}</p>
          </div>
        ))}
      </div>
      {data.blockedReason ? <p role="status" className="mt-3 font-medium">{data.blockedReason}</p>
        : !data.canRespond ? <p className="mt-3">Contatta la clinica o il laboratorio per rispondere.</p>
        : !data.row ? <p className="mt-3">La prescrizione non ha una prova disponibile. Contatta il laboratorio.</p>
        : <p className="mt-3">{data.requiresReplan
          ? 'Riseleziona la prova e indica una nuova data e ora di consegna. Controlla modalità impronta, note e allegati richiesti.'
          : 'Abbiamo mantenuto prova, data, ora e modalità attuali. Controlla i dati e conferma l’invio a PrimoUp.'}</p>}
      {actionable && (expanded ? <IsoActionForm data={data} onCancel={() => setExpanded(false)} onSubmit={async (values, files) => {
        // Keep saved IDs across retries; an upload error never recreates a file or the phase.
        for (const file of files) {
          let attachmentId = uploaded.current.get(file)
          if (!attachmentId) {
            const response = await fetch(await uploadUrl({}), { method: 'POST', headers: { 'Content-Type': file.type }, body: file })
            if (!response.ok) throw new Error(`Caricamento non riuscito: ${file.name}. Riprova.`)
            const { storageId } = await response.json()
            attachmentId = await saveFile({ prescriptions_id: prescriptionId, file_id: storageId, filename: file.name, content_type: file.type, file_size: file.size })
            if (!attachmentId) throw new Error('Allegato non salvato. Riprova.')
            uploaded.current.set(file, attachmentId)
          }
          const result = await syncFile({ attachmentId })
          if (result.status !== 'synced') throw new Error(`${file.name}: ${result.message || 'invio a PrimoUp ancora da completare'}. Il file è salvato nella prescrizione; riprova l’invio.`)
        }
        const result = await confirm({ prescription_id: prescriptionId, ...values })
        setNotice(result.status === 'awaiting_approval' ? 'Risposta salvata, in attesa del laboratorio.' : 'Risposta salvata. Sincronizzazione con PrimoUp in corso.')
        setExpanded(false)
      }} /> : <Button type="button" className="mt-4" onClick={() => setExpanded(true)}>
        {data.requiresReplan ? 'Scegli prova e nuova consegna' : 'Controlla e conferma i dati'}
      </Button>)}
      {notice && <p role="status" className="mt-3">{notice}</p>}
      {fromChat && <Link to={`/prescriptions/${prescriptionId}?iso=1#iso-action`} onClick={onOpenPrescription} className="mt-4 inline-block font-medium underline underline-offset-2">Apri prescrizione completa</Link>}
    </section>
  )
}

function revision(data: ActionData) {
  return `${data.row?._id}:${data.row?.updated_at}:${data.reports.filter(report => report.action_status === 'required').map(report => report._id).sort().join(',')}`
}

/** The form retains its snapshot and rejects changed reports instead of acknowledging unseen requests. */
export function IsoActionForm({ data, onSubmit, onCancel }: {
  data: ActionData; onSubmit: (values: ActionValues, files: File[]) => Promise<void>; onCancel: () => void
}) {
  const [initial] = useState(data)
  const row = initial.row!
  const fields = row.dataapp ? romeAppointmentFields(row.dataapp) : null
  const prefix = useId()
  const [phase, setPhase] = useState(initial.requiresReplan ? '' : String(row.prova_id ?? ''))
  const [date, setDate] = useState(initial.requiresReplan ? '' : fields?.date ?? '')
  const [time, setTime] = useState(row.timeapp || fields?.time || '')
  const [mode, setMode] = useState(row.scansione_intraorale ? 'digital' : row.presa_fisica_corriere ? 'physical' : '')
  const [notes, setNotes] = useState(row.note || '')
  const [files, setFiles] = useState<File[]>([])
  const [pending, setPending] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const sending = useRef(false)
  const stale = revision(data) !== revision(initial)
  const control = 'min-h-[44px] w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-base text-gray-900 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring'
  return <form className="mt-4 space-y-4 text-gray-900" aria-label="Risposta alla segnalazione ISO" onSubmit={async event => {
    event.preventDefault()
    if (sending.current || stale) return
    setError(null)
    try {
      parseRomeAppointment(date, time)
      if (!phase || !mode) throw new Error('Seleziona la prova e la modalità impronta.')
      for (const file of files) {
        const invalid = getPrimoUpAttachmentValidationError({ filename: file.name, contentType: file.type })
        if (invalid) throw new Error(`${file.name}: ${invalid}`)
      }
      sending.current = true
      setPending(true)
      await onSubmit({ report_ids: initial.reports.filter(report => report.action_status === 'required').map(report => report._id),
        expected_row_id: row._id, expected_row_updated_at: row.updated_at, prova_id: Number(phase),
        delivery_date: date, delivery_time: time, scansione_intraorale: mode === 'digital', notes }, files)
    } catch (cause) {
      setError(cause instanceof ConvexError ? String(cause.data) : cause instanceof Error ? cause.message : 'Invio non riuscito. Riprova.')
    } finally { sending.current = false; setPending(false) }
  }}>
    <p id={`${prefix}-hint`} className="text-sm">Data e ora si riferiscono al fuso italiano. Se la consegna è già passata, scegli un nuovo appuntamento.</p>
    <fieldset disabled={pending || stale} className="grid min-w-0 gap-4" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 180px), 1fr))' }} aria-describedby={`${prefix}-hint`}>
      <div className="min-w-0 col-span-full">
        <label htmlFor={`${prefix}-phase`} className="mb-1 block font-medium">Prova da eseguire</label>
        <select id={`${prefix}-phase`} required value={phase} className={control} onChange={event => setPhase(event.target.value)}>
          <option value="" disabled>Seleziona la prova</option>
          {initial.phases.map(item => <option key={item.id} value={item.id}>{item.name}</option>)}
        </select>
        {initial.requiresReplan && <p className="mt-1 text-xs">Prova precedente: {row.tipo_prova || 'non indicata'}. Puoi selezionarla di nuovo.</p>}
      </div>
      <div className="min-w-0">
        <label htmlFor={`${prefix}-date`} className="mb-1 block font-medium">Data di consegna</label>
        <Input id={`${prefix}-date`} required type="date" min={romeAppointmentFields(Date.now()).date} value={date} className="min-w-0 bg-white text-base" onChange={event => setDate(event.target.value)} />
      </div>
      <div className="min-w-0">
        <label htmlFor={`${prefix}-time`} className="mb-1 block font-medium">Ora di consegna</label>
        <Input id={`${prefix}-time`} required type="time" value={time} className="bg-white text-base" onChange={event => setTime(event.target.value)} />
      </div>
      <div className="min-w-0 col-span-full">
        <label htmlFor={`${prefix}-mode`} className="mb-1 block font-medium">Modalità impronta</label>
        <select id={`${prefix}-mode`} required value={mode} className={control} onChange={event => setMode(event.target.value)}>
          <option value="" disabled>Seleziona modalità</option>
          <option value="physical">Impronta fisica / presa corriere</option>
          <option value="digital">Digitale / scansione intraorale</option>
        </select>
      </div>
      <div className="min-w-0 col-span-full">
        <label htmlFor={`${prefix}-notes`} className="mb-1 block font-medium">Note per il laboratorio</label>
        <textarea id={`${prefix}-notes`} value={notes} maxLength={1024} rows={3} className={control} onChange={event => setNotes(event.target.value)} />
      </div>
      <div className="min-w-0 col-span-full">
        <label htmlFor={`${prefix}-files`} className="mb-1 block font-medium">Allegati richiesti, se necessari</label>
        <input id={`${prefix}-files`} type="file" multiple accept={PRIMOUP_ATTACHMENT_ACCEPT} className="block w-full min-w-0 text-sm file:mr-2 file:min-h-11 file:rounded-md file:border file:border-gray-300 file:bg-white file:px-3" onChange={event => setFiles(Array.from(event.target.files ?? []))} />
        <p className="mt-1 text-xs">JPG, JPEG e PNG. Salvati nella prescrizione e inviati a PrimoUp prima della conferma.</p>
      </div>
    </fieldset>
    {stale && <p role="alert" className="font-medium text-red-800">Le segnalazioni o la prova sono cambiate. Chiudi e riapri il modulo per controllare i dati aggiornati.</p>}
    {error && <p role="alert" className="whitespace-pre-wrap break-words font-medium text-red-800">{error}</p>}
    <div className="flex flex-col-reverse gap-2 sm:flex-row sm:flex-wrap sm:justify-end">
      <Button type="button" variant="outline" disabled={pending} onClick={onCancel}>Chiudi</Button>
      <Button type="submit" className="h-auto min-h-11 max-w-full whitespace-normal" disabled={pending || stale}>{pending ? 'Invio in corso…' : 'Conferma e aggiorna PrimoUp'}</Button>
    </div>
  </form>
}
